import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Eye, User, X, CalendarCheck, Link2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Pagination from '../components/Pagination';
import AddPatient from '../components/modals/AddPatient';
import BookAppointmentModal from '../components/modals/BookAppointmentModal';
import { getMyPatients, getDoctorById } from '../redux/doctorSlice';
import { getPatientById } from '../redux/patientSlice';
import { listMocLinkRequests } from '../redux/mocLinkSlice';
import MocLinkRequestsPanel from '../components/moc/MocLinkRequestsPanel';
import { getCountries } from '../redux/masterSlice';
import { patientLimitReached, patientLimitMessage } from '../redux/subscriptionSlice';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../components/common/FilterPanel';
import CommonLoader from '../components/CommonLoader';
import ErrorPage from '../components/ErrorPage';
import { SlidersHorizontal } from 'lucide-react';
import MasterLabel from '../components/common/MasterLabel';
import { getDoctorMissingSetupItems } from '../utils/doctorSetupStatus';
import { hospitalPatientCode } from '../utils/patientCode';

// Safe DOB formatter — handles ISO, YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY formats
const formatDob = (dob) => {
    if (!dob) return '-';
    let date;
    const str = String(dob).trim();
    // ISO format or YYYY-MM-DD
    if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) {
        date = new Date(str);
    }
    // DD/MM/YYYY or DD-MM-YYYY
    else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) {
        const parts = str.split(/[/-]/);
        date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    // Fallback
    else {
        date = new Date(str);
    }
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
import { getFileURL } from '../utils/fileURL';
import { emptyList as errorimg } from '../assets/img/illustrations';

export default function MyPatient() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(['patientpage']);
    const userRole = localStorage.getItem('Role');
    const doctorId = userRole === 'doctor' ? localStorage.getItem('ID') : '';
    const language = localStorage.getItem('i18nextLng');
    const isInitialCall = useRef(true);
    const { myPatients, myPatientsLoading, myPatientsTotal, myPatientsPage, myPatientsLimit, myPatientsTotalPages, error, selectedDoctor, loading: doctorLoading } = useSelector((state) => state.doctor);
    const { countries } = useSelector((state) => state.master);
    // MOC linking: the doctor's own link requests (mine=1) + socket-driven refresh.
    const { pendingCount: mocPendingCount, decidedTick: mocDecidedTick } = useSelector((state) => state.mocLink);
    const [view, setView] = useState('list'); // 'list' | 'links'
    // Subscription patient-limit gate (logged-in doctor's own plan + count).
    const subControls = useSelector((state) => state.subscription?.doctorAccess?.controls);
    const limitReached = patientLimitReached(subControls, myPatientsTotal);
    const limitMsg = patientLimitMessage(subControls);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [searchError, setSearchError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBookAppointmentModalOpen, setIsBookAppointmentModalOpen] = useState(false);
    const [selectedPatientForBooking, setSelectedPatientForBooking] = useState(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({ countryId: [], gender: [] });
    const [filters, setFilters] = useState({ countryId: [], gender: [] });
    const [refreshKey, setRefreshKey] = useState(0);

    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    // Fetch countries on mount
    useEffect(() => {
        dispatch(getCountries({ status: 'active', limit: 100 }));
    }, [dispatch]);

    // MOC linking — badge count, re-read when a patient answers an Approval
    // request in their app (socket → decidedTick).
    useEffect(() => {
        dispatch(listMocLinkRequests({ status: 'pending', mine: true, countOnly: true }));
    }, [dispatch, mocDecidedTick]);
    // An accepted link creates the patient row → refresh the list.
    const isFirstDecideRef = useRef(true);
    useEffect(() => {
        if (isFirstDecideRef.current) { isFirstDecideRef.current = false; return; }
        setRefreshKey((k) => k + 1);
    }, [mocDecidedTick]);

    // Validate search input
    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > 50) {
            return t('patientpage.search_exceed_limit', 'Search term cannot exceed 50 characters');
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('patientpage.search_only_spaces', 'Search term cannot contain only spaces');
        }

        return '';
    };

    // Debounce search term (only if no error)
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Only debounce if there's no error
        if (!searchError) {
            searchTimeoutRef.current = setTimeout(() => {
                // Trim the search term before setting it
                const trimmedSearch = searchTerm.trim();
                setDebouncedSearchTerm(trimmedSearch);
                setCurrentPage(1);
            }, 500);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, searchError]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Refocus search input after loading
    useEffect(() => {
        const active = document.activeElement === inputRef.current;
        if (!active && inputRef.current && searchTerm.length > 0) {
            inputRef.current.focus();
        }
    }, [myPatientsLoading]);

    // Fetch my patients
    useEffect(() => {
        // Don't fetch if there's a search error
        if (searchError) {
            return;
        }

        if (isInitialCall.current) {
            if (doctorId) {
                dispatch(getMyPatients({
                    doctorId,
                    page: currentPage,
                    limit: itemsPerPage,
                    search: debouncedSearchTerm,
                    gender: filters.gender,
                    countryId: filters.countryId
                }));
                // The setup gate below reads selectedDoctor from the store —
                // re-fetch on mount so profile edits (availability, payment &
                // bank) are reflected here without a hard refresh.
                dispatch(getDoctorById(doctorId));
            }
            isInitialCall.current = false;
            return;
        }

        // Subsequent calls on dependency changes
        if (doctorId) {
            const myPatientsParams = {
                doctorId,
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchTerm,
            };
            if (filters.gender.length > 0) myPatientsParams.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) myPatientsParams.countryId = filters.countryId.join(',');
            dispatch(getMyPatients(myPatientsParams));
        }
    }, [currentPage, itemsPerPage, debouncedSearchTerm, filters, doctorId, dispatch, searchError, refreshKey]);

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);
    const handleAddPatient = (result) => {
        handleCloseModal();
        // An Approval-channel MOC link was raised from the form: no row yet —
        // show where the request is waiting instead.
        if (result?.pendingLink) {
            setView('links');
            dispatch(listMocLinkRequests({ status: 'pending', mine: true, countOnly: true }));
            return;
        }
        // New patients sort to the top of page 1 (createdAt DESC), so jump back there.
        setCurrentPage(1);
        // Force a refetch so the newly created patient shows up instantly, even when
        // we're already on page 1 (where no state change would otherwise occur).
        setRefreshKey((k) => k + 1);
    };

    // Add patient → "Already registered here". A doctor may not have this
    // patient in their list yet, so search for them rather than navigate.
    const handleOpenExistingPatient = (p) => {
        const name = [p?.name, p?.surname].filter(Boolean).join(' ').trim();
        setView('list');
        if (name) {
            setSearchTerm(name.slice(0, 50));
            setSearchError('');
            setCurrentPage(1);
        }
        // Deliberately NOT "registered with you": the row exists at this
        // hospital, which is not the same as it being on this doctor's list —
        // the second half of the sentence is exactly why.
        toast.info(t('patientpage.already_registered_here_hint', 'This person already has a record here. Book an appointment to add them to your patients.'));
    };

    const handleBookClick = (patient) => {
        setSelectedPatientForBooking(patient);
        dispatch(getPatientById(patient.patientId || patient.id));
        if (doctorId) {
            dispatch(getDoctorById(doctorId));
        }
        setIsBookAppointmentModalOpen(true);
    };

    const handleCloseBookModal = () => {
        setIsBookAppointmentModalOpen(false);
        setSelectedPatientForBooking(null);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        // Enforce max length
        const finalValue = sanitized.length > 50
            ? sanitized.substring(0, 50)
            : sanitized;

        // Always update searchTerm so the input stays in sync
        setSearchTerm(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
        setCurrentPage(1);
    };

    // Format patients
    const formattedPatients = (myPatients || []).map((patient, index) => ({
        id: patient.patientId || index + 1,
        // THIS HOSPITAL'S number for the person (APO034) — it belongs to the
        // allocation, not to the person, since the same human carries a
        // different one at every hospital they visit. Null until the tenant
        // admin sets a prefix in Settings. `hospitalPatientCode` is the key
        // the server answers now; the helper still accepts the old alias.
        hospitalPatientCode: hospitalPatientCode(patient),
        name: `${patient.name || ''} ${patient.surname || ''}`.trim(),
        fullName: `${patient.name || ''} ${patient.surname || ''}`.trim(),
        image: patient.image,
        email: patient.email,
        phone: patient.phone,
        gender: patient.gender || '-',
        status: patient.status,
        dob: formatDob(patient.dob),
    }));

    const currentPatients = formattedPatients;
    const totalItems = myPatientsTotal;
    const indexOfFirstPatient = (currentPage - 1) * itemsPerPage;

    // Error handling
    if (error?.status >= 500 && error?.status <= 599) {
        return <ErrorPage error={error} pageName={t('patientpage.my_patients', 'My Patients')} />;
    }

    if (error?.message === 'Network Error') {
        return <ErrorPage error={error} pageName={t('patientpage.my_patients', 'My Patients')} />;
    }

    // Profile completion validation for doctors
    if (userRole === 'doctor') {
        // Get missing setup items using utility function
        const missingItemKeys = getDoctorMissingSetupItems(userRole, selectedDoctor);
        const missingItems = missingItemKeys.map(key => t(key));

        // If any required items are missing, show restriction page
        if (missingItems.length > 0) {
            return (
                <div className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                    <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                        <img src={errorimg} alt={t('profileValidation.altText')} className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain" />
                        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">{t('profileValidation.title')}</h2>
                        <p className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                            {t('profileValidation.message')}
                        </p>
                        <div className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4 sm:mb-6 md:mb-8">
                            <p className="text-xs sm:text-sm md:text-base text-gray-700 font-semibold mb-2">{t('profileValidation.missingItems')}:</p>
                            <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm md:text-base text-gray-600">
                                {missingItems.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        <button data-testid="my-patient-go-to-profile-button" onClick={() => navigate('/doctorProfile')} className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:from-[#3aa8a8] hover:to-[#2a7b7b] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 cursor-pointer">
                            {t('profileValidation.goToProfile')}
                        </button>
                    </div>
                </div>
            );
        }
    }

    return (
        <div data-testid="my-patient-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header Row */}
            <div data-testid="my-patient-page-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="my-patient-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {t('patientpage.my_patients', 'My Patients')}
                </h1>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    {/* MOC linking: this doctor's link requests (Approval ones wait
                        for the patient's app; OTP ones can be finished there). */}
                    <button
                        data-testid="my-patient-pending-links-button"
                        onClick={() => setView(view === 'links' ? 'list' : 'links')}
                        aria-pressed={view === 'links'}
                        className={`group flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 rounded-xl shadow-sm transition-all duration-300 font-semibold text-sm ${view === 'links'
                            ? 'bg-[#4ABDBD] text-white'
                            : 'border border-[#4ABDBD] text-[#3AA8A8] hover:bg-[#ecfdfd]'}`}
                    >
                        <Link2 className="w-5 h-5" />
                        <span>{t('patientpage.pending_links', 'Pending links')}</span>
                        {mocPendingCount > 0 && (
                            <span data-testid="my-patient-pending-links-badge" className={`rounded-full px-1.5 py-px text-[10px] font-bold ${view === 'links' ? 'bg-white text-[#2D8A8A]' : 'bg-[#4ABDBD] text-white'}`}>
                                {mocPendingCount}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid="add-patient-button"
                        onClick={handleOpenModal}
                        disabled={myPatientsLoading || limitReached}
                        title={limitReached ? limitMsg : ''}
                        className={`group flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${(myPatientsLoading || limitReached) ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}`}
                    >
                        <Plus data-testid="add-patient-icon" className="w-5 h-5" />
                        <span data-testid="add-patient-text">{t('patientpage.add_patient', 'Add Patient')}</span>
                    </button>
                </div>
            </div>

            {view === 'links' ? (
                <MocLinkRequestsPanel mine />
            ) : (<>
            {/* Search + Filter Row */}
            <div data-testid="search-container" className="mb-6 flex flex-col sm:flex-row gap-2">
                <div className="relative w-full sm:w-80">
                    <input
                        ref={inputRef}
                        data-testid="search-input"
                        type="text"
                        placeholder={t('patientpage.search_placeholder', 'Search patients...')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        disabled={myPatientsLoading}
                        maxLength={50}
                        className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 ${searchError
                            ? 'border-red-500 focus:ring-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-[#4ABDBD]'
                            } ${myPatientsLoading ? "opacity-70" : ""}`}
                    />
                    <Search data-testid="search-icon" className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4ABDBD]" />
                    {searchError && (
                        <p data-testid="my-patient-search-error" className="text-red-600 text-xs mt-1 absolute -bottom-5 left-0">{searchError}</p>
                    )}
                </div>

                {/* Filter Button + Popup */}
                <div data-testid="filter-container" className="relative w-full sm:w-auto">
                    <button
                        data-testid="my-patient-filter-button"
                        ref={filterButtonRef}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="group flex items-center justify-center gap-2  px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        aria-label={t('patientpage.filter')}
                        aria-expanded={isFilterOpen}
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                        {/* {t('patientpage.filter')} */}
                    </button>

                    {isFilterOpen && (
                        <FilterPopover ref={filterRef}>
                            <FilterPanel
                                onClear={() => {
                                    setTempFilters({ countryId: [], gender: [] });
                                    setFilters({ countryId: [], gender: [] });
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                onApply={() => {
                                    setFilters(tempFilters);
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                clearLabel={t('patientpage.clear')}
                                applyLabel={t('patientpage.apply')}
                            >
                                <FilterSection title={t('patientpage.country')} isActive={tempFilters.countryId.length > 0}>
                                    <FilterCountryList
                                        multiple
                                        value={tempFilters.countryId}
                                        onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                        countries={countries}
                                        searchPlaceholder={t('common.search_country', 'Search country...')}
                                    />
                                </FilterSection>

                                <FilterSection title={t('patientpage.gender')} isActive={tempFilters.gender.length > 0}>
                                    <FilterOptionList
                                        multiple
                                        value={tempFilters.gender}
                                        onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                        options={[
                                            { value: 'male', label: t('patientpage.male') },
                                            { value: 'female', label: t('patientpage.female') },
                                            { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                                            { value: 'other', label: t('patientpage.other') },
                                        ]}
                                    />
                                </FilterSection>
                            </FilterPanel>
                        </FilterPopover>
                    )}
                </div>
            </div>

            {/* ---- Applied Filters Display ---- */}
            {(filters.countryId.length > 0 || filters.gender.length > 0) && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2">
                            {t('patientpage.applied_filters', 'Applied Filters')}:
                        </span>

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} data-testid={`my-patient-applied-filter-country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('patientpage.country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    data-testid={`my-patient-applied-filter-country-remove-${cid}`}
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} data-testid={`my-patient-applied-filter-gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('patientpage.gender')}:</span> {t(`patientpage.${g}`)}
                                </span>
                                <button
                                    data-testid={`my-patient-applied-filter-gender-remove-${g}`}
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        <button
                            data-testid="my-patient-clear-all-filters-button"
                            onClick={() => {
                                setFilters({ countryId: [], gender: [] });
                                setTempFilters({ countryId: [], gender: [] });
                                setCurrentPage(1);
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('patientpage.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {myPatientsLoading ? (
                <div data-testid="my-patient-loading-state" className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span data-testid="my-patient-loading-text" className="text-xl text-[#4ABDBD]">{t('patientpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="patients-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.serial_no', 'Serial No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.full_name', 'Full Name')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.email', 'Email')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.phone', 'Phone')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.dob', 'Date of Birth')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.gender', 'Gender')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('patientpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPatients.length > 0 ? (
                                    currentPatients.map((patient, index) => {
                                        const isInactive = patient.status === 'inactive';
                                        return (
                                            <tr key={patient.id} data-testid={`my-patient-row-${patient.id}`} className={`border-b border-gray-100/50 transition-all duration-300 group ${isInactive ? 'bg-gray-100/50' : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}>
                                                <td data-testid={`my-patient-serial-${patient.id}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>{indexOfFirstPatient + index + 1}</td>
                                                <td data-testid={`my-patient-name-cell-${patient.id}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative w-10 h-10 flex-shrink-0">
                                                            {patient.image ? (
                                                                <img
                                                                    data-testid={`my-patient-image-${patient.id}`}
                                                                    src={getFileURL(patient.image)}
                                                                    alt={patient.fullName || t('patientpage.patient_alt', 'Patient')}
                                                                    className={`w-10 h-10 rounded-full object-cover shadow-md ${isInactive ? 'opacity-60' : ''}`}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                        if (fallback) fallback.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div
                                                                className={`image-fallback ${patient.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full ${isInactive ? 'bg-gray-400' : 'bg-[#4ABDBD]'} items-center justify-center shadow-md`}
                                                            >
                                                                {patient.fullName ? (
                                                                    <span className="text-white text-sm font-bold uppercase">
                                                                        {patient.fullName.charAt(0).toUpperCase()}
                                                                    </span>
                                                                ) : (
                                                                    <User className="w-5 h-5 text-white" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="inline-flex flex-wrap items-center gap-1.5">
                                                            <span data-testid={`my-patient-name-${patient.id}`}>{patient.fullName || '-'}</span>
                                                            {patient.hospitalPatientCode && (
                                                                <span data-testid={`my-patient-code-${patient.id}`} className="text-[10px] font-mono font-semibold text-[#2D8A8A] bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 rounded px-1.5 py-0.5">
                                                                    {patient.hospitalPatientCode}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td data-testid={`my-patient-email-${patient.id}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>{patient.email || '-'}</td>
                                                <td data-testid={`my-patient-phone-${patient.id}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>{patient.phone || '-'}</td>
                                                <td data-testid={`my-patient-dob-${patient.id}`} className={`p-3 text-xs transition-colors duration-300 ${isInactive ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-700'}`}>{formatDob(patient.dob)}</td>
                                                <td className="p-3 text-xs">
                                                    <span data-testid={`my-patient-gender-${patient.id}`} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${isInactive ? 'opacity-60' : ''}`} style={isInactive ? {
                                                        backgroundColor: '#9CA3AF15',
                                                        color: '#9CA3AF',
                                                        borderColor: '#9CA3AF30'
                                                    } : {
                                                        background: 'linear-gradient(to bottom right, rgba(74, 189, 189, 0.1), rgba(58, 155, 155, 0.1))',
                                                        color: '#3AA8A8',
                                                        borderColor: 'rgba(74, 189, 189, 0.2)'
                                                    }}>
                                                        {t(`patientpage.${patient.gender.toLowerCase()}`, patient.gender)}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-xs">
                                                    {isInactive ? (
                                                        <div data-testid={`my-patient-inactive-status-${patient.id}`} className="flex items-center justify-center">
                                                            <span className="text-gray-400 text-xs font-medium">{t('patientpage.inactive', 'Inactive')}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                data-testid={`my-patient-view-button-${patient.id}`}
                                                                onClick={() => navigate('/patientProfile', { state: { patientId: patient.id } })}
                                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                <Eye className="h-4 w-4 relative z-10" />
                                                            </button>
                                                            <button
                                                                data-testid={`my-patient-book-button-${patient.id}`}
                                                                onClick={() => handleBookClick(patient)}
                                                                className="text-white px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] shadow hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-105 text-xs font-semibold"
                                                            >
                                                                {t('patientpage.book', 'Book')}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr data-testid="my-patient-no-data-row">
                                        <td colSpan="7" data-testid="my-patient-no-data-text" className="p-3 text-center text-gray-500">
                                            {t('patientpage.no_patients_found', 'No patients found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Cards */}
                    <div data-testid="my-patient-mobile-cards" className="block sm:hidden space-y-4">
                        {currentPatients.length > 0 ? (
                            currentPatients.map((patient, index) => {
                                const isInactive = patient.status === 'inactive';
                                return (
                                    <div key={patient.id} data-testid={`my-patient-mobile-card-${patient.id}`} className={`p-4 rounded-xl shadow-md transition-all duration-300 ${isInactive ? 'bg-gray-100/50' : 'bg-gradient-to-r bg-gray-50 hover:shadow-lg'}`}>
                                        <div className="flex justify-between items-center mb-2">
                                            <span data-testid={`my-patient-mobile-serial-${patient.id}`} className={`text-xs font-bold ${isInactive ? 'text-gray-400' : 'text-[#4ABDBD]'}`}>
                                                {t('patientpage.serial_no', 'Serial No')}: {indexOfFirstPatient + index + 1}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {isInactive ? (
                                                    <span data-testid={`my-patient-mobile-inactive-${patient.id}`} className="text-gray-400 text-xs font-medium">{t('patientpage.inactive', 'Inactive')}</span>
                                                ) : (
                                                    <>
                                                        <button
                                                            data-testid={`my-patient-mobile-view-button-${patient.id}`}
                                                            onClick={() => navigate('/patientProfile', { state: { patientId: patient.id } })}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Eye className="h-4 w-4 relative z-10" />
                                                        </button>
                                                        <button
                                                            data-testid={`my-patient-mobile-book-button-${patient.id}`}
                                                            onClick={() => handleBookClick(patient)}
                                                            title={t('patientpage.book', 'Book')}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <CalendarCheck className="h-4 w-4 relative z-10" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className={`flex items-center gap-3 text-sm font-semibold ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>
                                                <div className="relative w-10 h-10 flex-shrink-0">
                                                    {patient.image ? (
                                                        <img
                                                            src={getFileURL(patient.image)}
                                                            alt={patient.fullName || t('patientpage.patient_alt', 'Patient')}
                                                            className={`w-10 h-10 rounded-full object-cover shadow-md ${isInactive ? 'opacity-60' : ''}`}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className={`image-fallback ${patient.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full ${isInactive ? 'bg-gray-400' : 'bg-[#4ABDBD]'} items-center justify-center shadow-md`}
                                                    >
                                                        {patient.fullName ? (
                                                            <span className="text-white text-sm font-bold uppercase">
                                                                {patient.fullName.charAt(0).toUpperCase()}
                                                            </span>
                                                        ) : (
                                                            <User className="w-5 h-5 text-white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="font-bold">{t('patientpage.full_name', 'Full Name')}:</span> {patient.fullName || '-'}
                                                    {patient.hospitalPatientCode && (
                                                        <span className="ml-1.5 text-[10px] font-mono font-semibold text-[#2D8A8A] bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 rounded px-1.5 py-0.5">
                                                            {patient.hospitalPatientCode}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <span className="font-bold">{t('patientpage.email', 'Email')}:</span> {patient.email}
                                            </div>
                                            <div className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <span className="font-bold">{t('patientpage.phone', 'Phone')}:</span> {patient.phone}
                                            </div>
                                            <div className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <span className="font-bold">{t('patientpage.dob', 'Date of Birth')}:</span> {formatDob(patient.dob)}
                                            </div>
                                            <div className="text-xs">
                                                <span className="font-bold">{t('patientpage.gender', 'Gender')}:</span>
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ml-1 ${isInactive ? 'opacity-60' : ''}`} style={isInactive ? {
                                                    backgroundColor: '#9CA3AF15',
                                                    color: '#9CA3AF',
                                                    borderColor: '#9CA3AF30'
                                                } : {
                                                    background: 'linear-gradient(to bottom right, rgba(74, 189, 189, 0.1), rgba(58, 155, 155, 0.1))',
                                                    color: '#3AA8A8',
                                                    borderColor: 'rgba(74, 189, 189, 0.2)'
                                                }}>
                                                    {t(`patientpage.${patient.gender.toLowerCase()}`, patient.gender)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div data-testid="my-patient-mobile-no-data" className="p-3 text-center text-gray-500">
                                {t('patientpage.no_patients_found', 'No patients found')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Pagination */}
            {!myPatientsLoading && totalItems > 0 && (
                <Pagination
                    totalItems={totalItems}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    custombutton={currentPatients.length === 0 ? 'disable' : ''}
                />
            )}

            </>)}

            {/* Add Patient Modal */}
            <AddPatient isOpen={isModalOpen} onClose={handleCloseModal} onSubmit={handleAddPatient} onOpenPatient={handleOpenExistingPatient} />

            <BookAppointmentModal
                isOpen={isBookAppointmentModalOpen}
                onClose={handleCloseBookModal}
                doctor={selectedDoctor}
                loading={doctorLoading}
                onAppointmentBooked={handleCloseBookModal}
            />
        </div>
    );
}

